import React, { useEffect, useRef, useState } from 'react';

const CustomCursor = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const dotRef = useRef(null);
  const ringRef = useRef(null);

  // Positions
  const mousePos = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });
  const animationFrameId = useRef(null);

  useEffect(() => {
    // Detect touch-only / coarse pointer devices
    const checkTouch = () => {
      const isTouch = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(hover: none) and (pointer: coarse)').matches : false;
      setIsTouchDevice(isTouch);
    };

    checkTouch();
    window.addEventListener('resize', checkTouch, { passive: true });

    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
      return () => window.removeEventListener('resize', checkTouch);
    }

    const onMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
      if (!isVisible) setIsVisible(true);

      // Check if mouse is over interactive/clickable elements
      const target = e.target;
      if (target && target.closest) {
        const interactive = target.closest(
          'button, a, .try-button, .features-box, .rep-circle, [role="button"], input, select, textarea, .profile-icon, .menu-icon, .copy-icon, .bot-icon'
        );
        setIsHovered(!!interactive);
      }
    };

    const onMouseDown = () => setIsPressed(true);
    const onMouseUp = () => setIsPressed(false);
    const onMouseLeave = () => setIsVisible(false);
    const onMouseEnter = () => setIsVisible(true);

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mousedown', onMouseDown, { passive: true });
    window.addEventListener('mouseup', onMouseUp, { passive: true });
    document.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('mouseenter', onMouseEnter);

    // Smooth trailing animation loop
    const render = () => {
      // Lerp ring position toward mouse position (speed factor 0.2)
      ringPos.current.x += (mousePos.current.x - ringPos.current.x) * 0.2;
      ringPos.current.y += (mousePos.current.y - ringPos.current.y) * 0.2;

      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${mousePos.current.x}px, ${mousePos.current.y}px, 0) translate(-50%, -50%)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringPos.current.x}px, ${ringPos.current.y}px, 0) translate(-50%, -50%)`;
      }

      animationFrameId.current = requestAnimationFrame(render);
    };

    animationFrameId.current = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('resize', checkTouch);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('mouseenter', onMouseEnter);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [isVisible]);

  if (isTouchDevice) {
    return null;
  }

  return (
    <>
      {/* Inner 6px Solid Red Dot */}
      <div
        ref={dotRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '6px',
          height: '6px',
          backgroundColor: '#ff3131',
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 99999,
          opacity: isVisible ? 1 : 0,
          transform: 'translate(-100px, -100px)',
          transition: 'opacity 0.15s ease, transform 0.05s linear',
          boxShadow: '0 0 8px rgba(255, 49, 49, 0.8)',
        }}
      />

      {/* Outer 32px Tracking Ring */}
      <div
        ref={ringRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: isHovered ? '70px' : isPressed ? '26px' : '32px',
          height: isHovered ? '70px' : isPressed ? '26px' : '32px',
          border: isHovered
            ? '1.5px solid rgba(255, 49, 49, 0.9)'
            : '1.5px solid rgba(255, 49, 49, 0.6)',
          backgroundColor: isHovered
            ? 'rgba(255, 49, 49, 0.15)'
            : isPressed
            ? 'rgba(255, 49, 49, 0.25)'
            : 'transparent',
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 99998,
          opacity: isVisible ? 1 : 0,
          transform: 'translate(-100px, -100px)',
          transition:
            'width 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275), height 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275), border 0.2s ease, background-color 0.2s ease, opacity 0.2s ease',
          boxShadow: isHovered
            ? '0 0 15px rgba(255, 49, 49, 0.35)'
            : 'none',
        }}
      />
    </>
  );
};

export default CustomCursor;
